import {Box, Typography} from '@mui/material';
import React, {useMemo} from 'react';
import EfficiencyPolarChart from './EfficiencyPolarChart';
import {PlayerSession} from '../../../interfaces';
import {useTranslation} from 'react-i18next';
import {PositionGroup, Positions} from '../../../enums';
import {getUniqueName} from '../../../services/graphService';
import PageSubtitle from '../PageSubtitle';
import {getMean} from '../../../services/mathService';

interface Props {
  playerSessions: PlayerSession[];
  teamSessionStartTime: string;
  teamSessionEndTime: string;
  practiceEfficiencyMax?: number;
}

const PracticeEfficiency: React.FC<Props> = ({
  playerSessions,
  teamSessionStartTime,
  teamSessionEndTime,
  practiceEfficiencyMax,
}) => {
  const {t} = useTranslation();
  const {forwardData, defenseData} = useMemo(() => {
    const forwardData: {
      x: string;
      y: number;
    }[] = [];
    const defenseData: {
      x: string;
      y: number;
    }[] = [];
    const allPlayerNames = playerSessions.map(ps => ({
      firstName: ps.player?.profile?.firstName || 'Unknown',
      lastName: ps.player?.profile?.lastName || 'Unknown',
      profileId: ps.player?.profile?.id || 0,
    }));
    const totalSessionTime =
      (new Date(teamSessionEndTime).getTime() -
        new Date(teamSessionStartTime).getTime()) /
      1000;
    playerSessions
      .filter(ps => ps.activeTime && ps.activeTime > 0)
      .forEach(playerSession => {
        const playersPosition = playerSession.player?.profile?.position || '';
        const efficiencyValue =
          ((playerSession.strintTotalTime || 0) / totalSessionTime) * 100;
        const currentPlayerNameData = {
          firstName: playerSession.player?.profile?.firstName || 'Unknown',
          lastName: playerSession.player?.profile?.lastName || 'Unknown',
          profileId: playerSession.player?.profile?.id || 0,
        };
        const playerName = getUniqueName(currentPlayerNameData, allPlayerNames);
        const playerData = {
          x: playerName,
          y: efficiencyValue,
        };
        if (playersPosition === PositionGroup.defense) {
          defenseData.push(playerData);
        } else if (
          [Positions.center, Positions.leftwing, Positions.rightwing].includes(
            playersPosition as Positions,
          )
        ) {
          forwardData.push(playerData);
        }
      });
    forwardData.sort((a, b) => (b.x < a.x ? 1 : -1));
    defenseData.sort((a, b) => (b.x < a.x ? 1 : -1));
    return {
      forwardData,
      defenseData,
    };
  }, [playerSessions, teamSessionEndTime, teamSessionStartTime]);
  // determine max practice efficiency, default to 25 if missing
  const max = useMemo(
    () => practiceEfficiencyMax ?? 25,
    [practiceEfficiencyMax],
  );

  const mean = useMemo(() => {
    const teamData = [...forwardData, ...defenseData];
    const mean = getMean(teamData.filter(d => d.y > 0).map(d => d.y));
    return mean;
  }, [forwardData, defenseData]);

  return (
    <Box sx={{mb: 5}}>
      <PageSubtitle>{t('practiceEfficiency.title')}</PageSubtitle>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: {xs: 'column', md: 'row'},
        }}>
        <EfficiencyPolarChart
          data={forwardData}
          practiceEfficiencyMax={max}
          positionGroup={PositionGroup.forwards}
        />
        <EfficiencyPolarChart
          data={defenseData}
          practiceEfficiencyMax={max}
          positionGroup={PositionGroup.defense}
        />
      </Box>
      {mean && (
        <Typography
          variant="h4"
          sx={{
            mb: 2,
            textAlign: 'center',
          }}>{`${t('practiceEfficiency.teamAverage')}: ${mean.toFixed(1)}%`}</Typography>
      )}
      <Typography variant="body1" sx={{textAlign: 'center'}}>
        {t('practiceEfficiency.info')}
      </Typography>
    </Box>
  );
};

export default PracticeEfficiency;
